import { queryOptions, useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/api-context';
import { useMemo } from 'react';
/**
 * Fetches External accounts.
 *
 * @param {ReturnType<typeof useApi>} api - API instance for making requests.
 * @returns {Promise<ExternalAccount[]>} - A promise resolving to the list of accounts.
 */
const fetchExternalAccounts = async (api) => {
    const response = await api.get('/external-account/account');
    return response.data;
};
/**
 * Exposed query options for fetching External accounts.
 *
 * @param {ReturnType<typeof useApi>} api - API instance.
 * @returns Query options for React Query.
 */
export const getExternalAccountsQueryOptions = (api) => queryOptions({
    queryKey: ['external-accounts'],
    queryFn: () => fetchExternalAccounts(api),
});
/**
 * React Query hook for fetching External accounts.
 *
 * @param {UseExternalAccountsOptions} options - Optional configuration for the query.
 * @returns React Query response object.
 */
export const useGetExternalAccountsApi = ({ queryConfig } = {}) => {
    const api = useApi();
    return useQuery({
        ...getExternalAccountsQueryOptions(api),
        ...queryConfig,
    });
};
/**
 * Hook to get External accounts.
 *
 * @returns {ExternalAccount[]} - The list of External accounts.
 */
export const useExternalAccounts = () => {
    const { data: accounts = [] } = useGetExternalAccountsApi();
    return accounts;
};
/**
 * Hook to get External accounts filtered by provider.
 * Reuses the existing external accounts data and filters client-side.
 *
 * @param {ExternalAccountProvider} provider - The provider to filter by.
 * @returns {ExternalAccount[]} - The list of External accounts for the specified provider.
 */
export const useExternalAccountsByProvider = (provider) => {
    const accounts = useExternalAccounts();
    return useMemo(() => {
        if (!provider) {
            return accounts;
        }
        return accounts.filter((account) => account.provider === provider);
    }, [accounts, provider]);
};
